import { Box, HStack, Stack, Text, Icon } from "@chakra-ui/react";
import {
  KYDPrice,
  KYDSubscription,
  KYDSubscriptionItem,
} from "@common/interfaces/KYDBilling";
import { FiMail, FiMessageSquare } from "react-icons/fi";
import { Price } from "../Dashboard/types";
import { formatDate } from "../../utils";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { DisplaySubscriptionItem } from "@common/interfaces/KYDBilling";

interface PlanCardProps {
  subscription: KYDSubscription;
  item: DisplaySubscriptionItem;
  usageData?: {
    delivered: number;
    freeCreditsUsed: number;
    spend: number;
    period: string;
  };
}

dayjs.extend(utc);

// Channel-specific components
const ChannelIcon: React.FC<{ channel: string }> = ({ channel }) => {
  const getIcon = () => {
    if (channel === "EMAIL_CAMPAIGN") return FiMail;
    if (channel === "SMS_CAMPAIGN") return FiMessageSquare;
    return FiMail; // default fallback
  };

  return <Icon as={getIcon()} boxSize={5} color="gray.500" />;
};

const PlanDisplayName: React.FC<{ channel: string }> = ({ channel }) => {
  const getDisplayName = () => {
    if (channel === "EMAIL_CAMPAIGN") return "Email Plan";
    if (channel === "SMS_CAMPAIGN") return "SMS Plan";
    return "Plan";
  };

  return (
    <Text fontSize="lg" fontWeight="semibold" color="gray.700">
      {getDisplayName()}
    </Text>
  );
};

const getUsageUnitLabel = (usage_unit: string) => {
  if (usage_unit === "email_send") return "emails sent";
  if (usage_unit === "sms_send") return "SMS sent";
  return "units";
};

const UsageCost: React.FC<{ item: DisplaySubscriptionItem }> = ({ item }) => {
  const getUsageCost = () => {
    return `${item.first_paid_tier_price} per ${
      item.price?.unit_quantity
    } ${getUsageUnitLabel(item.price?.usage_unit)}`;
  };

  return (
    <HStack justify="space-between" align="center">
      <Text fontSize="sm" color="gray.600" fontWeight="medium">
        Usage Cost
      </Text>
      <Text fontSize="sm" color="gray.700" textAlign="right">
        {getUsageCost()}
      </Text>
    </HStack>
  );
};

const FreeCredits: React.FC<{
  item: DisplaySubscriptionItem;
}> = ({ item }) => {
  return (
    <HStack justify="space-between" align="center">
      <Text fontSize="sm" color="gray.600" fontWeight="medium">
        Total Free Credits
      </Text>
      <Text fontSize="sm" color="gray.700" textAlign="right">
        {item.display_free_tier_available_quantity}
      </Text>
    </HStack>
  );
};

const getDeliveredLabel = (channel: string) => {
  return "Delivered";
};

// Utility components
const Divider: React.FC = () => (
  <Box borderTop="1px solid" borderColor="gray.200" w="full" />
);

const MetricBox: React.FC<{
  label: React.ReactNode;
  value: React.ReactNode;
}> = ({ label, value }) => (
  <Box
    flex={1}
    bgGradient="linear(to-b, #E7E9EF03, #fff)"
    p={4}
    py={3}
    borderRadius="md"
    border="1px solid"
    boxShadow="0 8px 32px rgba(0, 0, 0, 0.03)"
    borderColor="gray.200"
    textAlign="left"
    display="flex"
    flexDirection="column"
    justifyContent="center"
  >
    <Stack spacing={1}>
      <Text fontSize="sm" color="gray.700" fontWeight="medium" noOfLines={1}>
        {label}
      </Text>
      <Text fontSize="2xl" color="gray.700">
        {value}
      </Text>
    </Stack>
  </Box>
);

// Formatting utilities
const formatNumber = (num: number) => num.toLocaleString();

export const PlanCard: React.FC<PlanCardProps> = ({ subscription, item }) => {
  return (
    <Box
      bg="white"
      p={6}
      borderRadius="lg"
      border="1px solid"
      borderColor="gray.200"
      w="full"
    >
      {/* Header */}
      <HStack justify="space-between" align="center" mb={4}>
        <PlanDisplayName channel={item.type} />
        <ChannelIcon channel={item.type} />
      </HStack>

      {/* Details */}
      <Stack spacing={3}>
        <Divider />
        <UsageCost item={item} />
        {item.free_tier_available_quantity > 0 && (
          <>
            <Divider />
            <FreeCredits item={item} />
          </>
        )}

        {/* Current Usage Section */}
        {item.usage_quantity !== undefined && item.usage_quantity !== null && (
          <>
            <Divider />

            {/* Current Usage Header */}
            <HStack justify="space-between" align="center">
              <Text fontSize="sm" color="gray.600" fontWeight="medium">
                Current Usage
              </Text>
              <Text fontSize="sm" color="gray.700" textAlign="right">
                Period:{" "}
                {dayjs.utc(subscription.current_period_start).format("MMM D")}–
                {dayjs
                  .utc(subscription.current_period_end)
                  .format("MMM D YYYY")}
              </Text>
            </HStack>

            {/* Usage Metrics */}
            <Box overflowX="auto" w="full">
              <HStack spacing={3} w="full" minW="max-content">
                <MetricBox
                  label={getDeliveredLabel(item.type)}
                  value={formatNumber(item.usage_quantity)}
                />
                {item.display_free_tier_usage_remaining ? (
                  <MetricBox
                    label="Free Credits Remaining"
                    value={item.display_free_tier_usage_remaining}
                  />
                ) : null}
                <MetricBox label="Spend" value={item.display_spend ?? "$0"} />
              </HStack>
            </Box>
          </>
        )}
      </Stack>
    </Box>
  );
};
